import type React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Tooltip as TTip } from 'react-tooltip'

import Flag from '../../../assets/images/icons/flag.svg?react'
import HelpIcon from '../../../assets/images/icons/help-icon.svg?react'
import ArrowDown from '../../../assets/images/icons/min-arrow-down.svg?react'
import type { BreakdownInfo } from '../../../store/types/company'
import Tooltip from '../../../utils/tool-tip'
import { LeftArrow, RightArrow } from '../../icons/general'

interface TableProps {
  data?: BreakdownInfo[]
  sortData?: any
  sortColumn?: any
  sortDirection?: any
}

const SitesTable: React.FC<TableProps> = ({
  data,
  sortData,
  sortColumn,
  sortDirection,
}) => {
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [pagination, setPagination] = useState(1)
  const [totalPage, setTotalPage] = useState(1)

  const handleNextClick = () => {
    if (pagination < totalPage) {
      setPagination(pagination + 1)
      setPage(page + 10)
    }
  }
  const handlePrevClick = () => {
    if (pagination > 1) {
      setPagination(pagination - 1)
      setPage(page - 10)
    }
  }

  useEffect(() => {
    setPage(0)
    setPagination(1)
  }, [data])

  useEffect(() => {
    setTotalPage(data?.length ? Math.ceil(data?.length / 10) : 1)
  }, [data, page])

  return (
    <div className="flex flex-col">
      <div className="border-b">
        <div className="flex flex-row pl-6 pr-12 py-3 font-medium text-xs text-gray-500 whitespace-nowrap">
          <button
            onClick={() => sortData('url')}
            className="flex md:w-1/3 w-1/2"
          >
            <span className="pr-2">Company</span>
            <ArrowDown
              className={`${
                sortColumn === 'url' && sortDirection === 'asc'
                  ? 'rotate-180'
                  : ''
              }`}
            />
          </button>
          <button
            onClick={() => sortData('match_percentage')}
            className="flex flex-row md:w-1/3 w-1/2 pl-4"
          >
            <span className="pr-2">Match Rate</span>
            <div className="pr-2">
              <Tooltip text="Sort by Matching Rate">
                <HelpIcon />
              </Tooltip>
            </div>
            <ArrowDown
              className={`${
                sortColumn === 'match_percentage' && sortDirection === 'asc'
                  ? 'rotate-180'
                  : ''
              }`}
            />
          </button>
          <button
            onClick={() => sortData('c')}
            className="md:flex hidden w-1/5 pl-14"
          >
            <span className="pr-2">Product Count</span>
            <ArrowDown
              className={`${
                sortColumn === 'c' && sortDirection === 'asc'
                  ? 'rotate-180'
                  : ''
              }`}
            />
          </button>
          <button
            onClick={() => sortData('prisync_index')}
            className="md:flex hidden w-1/5 pl-20"
          >
            <span className="pr-2">Index</span>
            <div className="pr-2">
              <Tooltip text="Sort by Index">
                <HelpIcon />
              </Tooltip>{' '}
            </div>
            <ArrowDown
              className={`${
                sortColumn === 'prisync_index' && sortDirection === 'asc'
                  ? 'rotate-180'
                  : ''
              }`}
            />
          </button>
        </div>
      </div>

      <div className="flex flex-col">
        {data?.slice(page, page + 10).map((row, i) => (
          <div
            key={`company_row_${i}`}
            className={`flex flex-row justify-between px-6 w-full border-b text-gray-500 ${
              row.mine
                ? 'bg-yellow-100'
                : i % 2 === 0
                  ? 'bg-gray-50'
                  : 'bg-white-100'
            }`}
          >
            <button
              type="button"
              onClick={() =>
                navigate(
                  row.mine
                    ? '/products?page=1&limit=10&filter_ismine=1'
                    : `/products?page=1&limit=10&orderBy=name&orderDirection=asc&company=${row.s}&company_filter_operator=and`,
                )
              }
              className="md:w-1/3 w-1/2 text-left overflow-hidden whitespace-nowrap text-ellipsis font-medium text-sm py-6 pr-1"
            >
              <div
                className="inline"
                data-tooltip-id="url-info"
                data-tooltip-content={row.url}
                data-tooltip-place="top-start"
              >
                {row.url}{' '}
                {row.mine ? <Flag className="ml-1 inline-block" /> : null}
              </div>
            </button>
            <div className="flex flex-row md:w-1/3 w-1/2 py-6 text-gray-900">
              <div className="flex w-full bg-gray-200 rounded-full h-2.5 mt-2 mr-3">
                <div
                  className="flex bg-primary-600 rounded-full h-2.5"
                  style={{ width: `${row.match_percentage}%` }}
                ></div>
              </div>
              <span className="font-medium text-sm ">
                {row.match_percentage}%
              </span>
            </div>
            <div className="md:flex w-1/5 pl-14 py-6 hidden font-normal text-sm">
              {row.c}
            </div>
            <div className="md:flex hidden w-1/5 pl-16 py-6 font-normal text-sm ">
              {row?.prisync_index && row?.prisync_index}
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-row justify-between container mx-auto px-4 lg:px-6 pt-3 pb-4">
        <button
          onClick={handlePrevClick}
          className="hidden sm:flex items-center justify-center border border-solid text-gray-900 border-gray-300 rounded-lg w-20 h-9 disabled:border-gray-200 disabled:text-gray-200"
          disabled={pagination === 1}
        >
          <span className="font-medium text-sm ">Previous</span>
        </button>
        <button
          onClick={handlePrevClick}
          className="flex sm:hidden items-center justify-center border border-solid border-gray-300 rounded-lg w-9 h-9 disabled:border-gray-200 disabled:text-gray-200"
          disabled={pagination === 1}
        >
          <LeftArrow stroke={pagination === 1 ? '#EAECF0' : '#344054'} />
        </button>
        <span className="font-medium text-sm pt-2">
          Page {pagination} of {totalPage}
        </span>
        <button
          onClick={handleNextClick}
          className="hidden sm:flex items-center justify-center border border-solid text-gray-900 border-gray-300 rounded-lg w-16 h-9 disabled:border-gray-200 disabled:text-gray-200"
          disabled={pagination === totalPage}
        >
          <span className="font-medium text-sm ">Next</span>
        </button>
        <button
          onClick={handleNextClick}
          className="flex sm:hidden items-center justify-center border border-solid border-gray-300 rounded-lg w-9 h-9 disabled:border-gray-200 disabled:text-gray-200"
          disabled={pagination === totalPage}
        >
          <RightArrow
            stroke={pagination === totalPage ? '#EAECF0' : '#344054'}
          />
        </button>
      </div>
      <TTip id="url-info" />
    </div>
  )
}

export default SitesTable
